import type { IconProps } from './icons';

export type NavKey = string;

export interface NavEntry {
  key: NavKey;
  label: string;
  /** Shorter label for the phone tab bar, where 68px is the whole budget. */
  short?: string;
  Icon: React.FC<IconProps>;
  /** Rendered below the rule, in the concept's dimmer ink. */
  footer?: boolean;
  /** Present in the sidebar only — the tab bar holds four, not nine. */
  desktopOnly?: boolean;
}

/**
 * The shell's navigation, in both of its forms.
 *
 * Presentational on purpose: these take `entries`/`active`/`onSelect` and know
 * nothing about routing. That is what lets design-system/ stay independently
 * bundleable — the boundary test in src/architecture.test.ts forbids a value
 * import of app code, and react-router would drag the app's history in.
 */

const Item: React.FC<{
  entry: NavEntry;
  active: boolean;
  onSelect: (key: NavKey) => void;
}> = ({ entry, active, onSelect }) => (
  <button
    type="button"
    onClick={() => onSelect(entry.key)}
    aria-current={active ? 'page' : undefined}
    data-testid={`nav-${entry.key}`}
    className={`flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-[12.5px] font-medium transition-colors ${
      active
        ? 'bg-accent text-accent-foreground'
        : entry.footer
          ? 'text-muted-foreground hover:bg-shell'
          : 'text-foreground-soft hover:bg-shell'
    }`}
  >
    <entry.Icon />
    {entry.label}
  </button>
);

export const Wordmark: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div
    className={`text-[13px] leading-none tracking-[0.36em] text-foreground ${className}`}
  >
    SIZEWALL
  </div>
);

/** The coach's own chip at the top of the sidebar. */
export const NavIdentity: React.FC<{
  name: string;
  detail: string;
  imageUrl?: string;
}> = ({ name, detail, imageUrl }) => {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join('');

  return (
    <div className="flex items-center gap-2.5">
      {imageUrl ? (
        <img
          src={imageUrl}
          alt=""
          className="h-[34px] w-[34px] shrink-0 rounded-full object-cover"
        />
      ) : (
        <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-primary text-[11px] font-semibold text-primary-foreground">
          {initials || '—'}
        </span>
      )}
      <div className="min-w-0">
        <div className="truncate text-[12.5px] font-semibold leading-tight">{name}</div>
        <div className="truncate font-mono text-[8.5px] font-medium uppercase leading-snug tracking-[0.08em] text-muted-foreground">
          {detail}
        </div>
      </div>
    </div>
  );
};

/** Desktop: the concept's 200px rail. */
export const SideNav: React.FC<{
  entries: NavEntry[];
  active: NavKey;
  onSelect: (key: NavKey) => void;
  identity?: React.ReactNode;
  /** Pinned to the bottom of the rail — the concept's CLIENT GOAL card. */
  footer?: React.ReactNode;
  onHome?: () => void;
  className?: string;
}> = ({ entries, active, onSelect, identity, footer, onHome, className = '' }) => {
  const main = entries.filter((e) => !e.footer);
  const below = entries.filter((e) => e.footer);

  return (
    <nav
      data-testid="side-nav"
      className={`flex w-[200px] shrink-0 flex-col gap-5 border-r border-divider bg-card px-[18px] py-[22px] ${className}`}
    >
      {onHome ? (
        <button
          type="button"
          onClick={onHome}
          aria-label="Start page"
          data-testid="side-nav-home"
          className="-mx-1 rounded-md px-1 py-1 text-left hover:bg-shell"
        >
          <Wordmark />
        </button>
      ) : (
        <Wordmark />
      )}
      {identity}
      <div className="flex flex-col gap-0.5">
        {main.map((e) => (
          <Item key={e.key} entry={e} active={e.key === active} onSelect={onSelect} />
        ))}
        {below.length > 0 && <div className="mx-3 my-2 h-px bg-divider" />}
        {below.map((e) => (
          <Item key={e.key} entry={e} active={e.key === active} onSelect={onSelect} />
        ))}
      </div>
      <div className="flex-1" />
      {footer}
    </nav>
  );
};

/**
 * Phone: a tab bar we own.
 *
 * `ion-tab-bar` is what this replaces, and the reason it had to go is that it
 * has no desktop form — it painted a 56px phone bar across a 2560px window.
 * This one simply is not rendered above `lg`.
 */
export const BottomTabs: React.FC<{
  entries: NavEntry[];
  active: NavKey;
  onSelect: (key: NavKey) => void;
  className?: string;
}> = ({ entries, active, onSelect, className = '' }) => (
  <nav
    data-testid="bottom-tabs"
    className={`sticky bottom-0 z-20 flex border-t border-divider bg-card pb-[env(safe-area-inset-bottom)] ${className}`}
  >
    {entries
      .filter((e) => !e.desktopOnly)
      .map((e) => {
        const on = e.key === active;
        return (
          <button
            key={e.key}
            type="button"
            onClick={() => onSelect(e.key)}
            aria-current={on ? 'page' : undefined}
            data-testid={`tab-${e.key}`}
            className={`flex flex-1 flex-col items-center gap-1 py-2.5 text-[10px] font-medium ${
              on ? 'text-primary' : 'text-muted-foreground'
            }`}
          >
            <e.Icon size={20} />
            {e.short ?? e.label}
          </button>
        );
      })}
  </nav>
);
